import { useState, useCallback, useEffect } from 'react'
import { useAsyncOperation } from './useAsyncOperation'
import { reviewsAPI } from '../services/api/reviews'
import { createLogger } from '../utils/logger'

const logger = createLogger('useEntityReviews')
const PAGE_SIZE = 10

export function useEntityReviews(entityId) {
  const [reviews, setReviews] = useState([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const { loading, execute } = useAsyncOperation()

  const fetchReviews = useCallback(async (pageNum = 1, append = false) => {
    if (!entityId) return
    try {
      const response = await execute(
        () => reviewsAPI.getEntityReviews(entityId, { page: pageNum, limit: PAGE_SIZE }),
        { silent: true }
      )
      if (!response) return
      const data = response.data
      const items = data.reviews || data.items || []
      const totalCount = data.total || 0
      setTotal(totalCount)
      setReviews(prev => append ? [...prev, ...items] : items)
      setHasMore(pageNum * PAGE_SIZE < totalCount)
      setPage(pageNum)
    } catch (err) {
      logger.error('Failed to fetch reviews', err)
    }
  }, [entityId, execute])

  const loadMore = useCallback(() => {
    fetchReviews(page + 1, true)
  }, [page, fetchReviews])

  const refetch = useCallback(() => {
    fetchReviews(1, false)
  }, [fetchReviews])

  useEffect(() => {
    fetchReviews(1, false)
  }, [fetchReviews])

  // Compute avg rating from loaded reviews (approximate — accurate for first page)
  const ratedReviews = reviews.filter(r => r.rating != null)
  const avgRating = ratedReviews.length > 0
    ? ratedReviews.reduce((sum, r) => sum + r.rating, 0) / ratedReviews.length
    : null

  return { reviews, loading, hasMore, total, avgRating, loadMore, refetch }
}
